/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import type { SelectedFileItem } from "@ServerTypes/folders/folders.schema";
import { useEffect, useRef, useState } from "react";
import { useTranslation, untranslated } from "@i18n";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import axiosWrapper from "@AxiosWrapper";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import _ from "lodash";
import type {
  GetParentOfConstituentFilesResponse,
  FileSystemServerDeleteResponse,
} from "@ServerTypes/fileUpload/fileUpload.schema";

/** A file/folder row this hook deletes; only these members are read. */
type DeleteTargetRow = SelectedFileItem & { name?: string };

/** One constituent parent, as `getParentOfConstituentFiles` reports it per file id. */
type ConstituentParent = { _id?: string; name?: string };

interface UseFileDeleteProps {
  selectedFiles: DeleteTargetRow[];
  workspaceId?: string;
  path?: string;
  isFileUpload?: boolean;
  /**
   * `totalDirectoryCount` is the folder tree's recomputed row count, present only when the caller opted in
   * via `requestDirectoryTotal`. Optional, so the callers that ignore it are unaffected.
   */
  onDeleteSuccess?: (success: boolean, totalDirectoryCount?: number) => void;
  onDeleteFiles?: () => void;
  onCloseModal?: () => void;
  requestDirectoryTotal?: boolean;
  directoryTotalProjectId?: string;
}

function useFileDelete({
  selectedFiles = [],
  workspaceId,
  path = "/",
  isFileUpload = false,
  onDeleteSuccess = () => {},
  onDeleteFiles = () => {},
  onCloseModal = () => {},
  requestDirectoryTotal = false,
  directoryTotalProjectId,
}: UseFileDeleteProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Ref to always have latest selectedFiles (avoids stale closure issues)
  const selectedFilesRef = useRef(selectedFiles);
  useEffect(() => {
    selectedFilesRef.current = selectedFiles;
  }, [selectedFiles]);

  // State
  const [constituentFilesMap, setConstituentFilesMap] = useState<Record<string, ConstituentParent[]>>({});
  const [showConstituentWarning, setShowConstituentWarning] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [deleteAssociatedTasks, setDeleteAssociatedTasks] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get resource type from first selected file
  const resourceType = _.get(selectedFiles, "[0].resourceType");
  const isSchedule = resourceType === "RESOURCE_TYPE_SCHEDULE";
  const isEstimate = resourceType === "RESOURCE_TYPE_ESTIMATE";
  const isContract = resourceType === "RESOURCE_TYPE_CONTRACT";
  const isFolder = resourceType === "RESOURCE_TYPE_FOLDER";

  // Determine selected resource label for UI
  const getSelectedResourceLabel = () => {
    if (selectedFiles.length === 0) return "file";

    if (isSchedule) return "schedule";
    if (isEstimate) return "estimate";
    if (isContract) return "contract";

    if (selectedFiles.length === 1) {
      if (isFolder) return "folder";
      return "file";
    }

    // Multiple files selected
    const checkIfFoldersAndFiles = { folder: false, file: false };
    selectedFiles.forEach((file: DeleteTargetRow) => {
      if (file.resourceType === "RESOURCE_TYPE_FOLDER") {
        checkIfFoldersAndFiles.folder = true;
      } else {
        checkIfFoldersAndFiles.file = true;
      }
    });

    if (checkIfFoldersAndFiles.file && checkIfFoldersAndFiles.folder) {
      return "items";
    } else if (!checkIfFoldersAndFiles.file && checkIfFoldersAndFiles.folder) {
      return "folders";
    }
    return "files";
  };

  const selectedResourceLabel = getSelectedResourceLabel();

  // Generate delete warning text
  const getDeleteWarningText = () => {
    if (isSchedule) {
      return "All constituent schedules will also get deleted. Are you sure you want to delete?";
    }
    const count = selectedFiles.length;
    const countText = count > 1 ? `${count} ` : "";
    const thisThese = count === 1 ? "this" : "these";
    return `Are you sure you want to delete ${thisThese} ${countText}${selectedResourceLabel}?`;
  };

  const deleteWarningText = getDeleteWarningText();

  // Check if files are constituent files
  const checkIfFileIsConstituentFile = async () => {
    if (isEstimate || isSchedule || isFileUpload) {
      return;
    }
    try {
      const fileIds = selectedFiles.map((file: DeleteTargetRow) => file.hierarchyId);
      const response = await axiosWrapper.post<GetParentOfConstituentFilesResponse>(
        `${getBackendUrl()}/fileUpload/getParentOfConstituentFiles`,
        {
          fileId: fileIds,
        },
      );
      setConstituentFilesMap((response.data as { data?: Record<string, ConstituentParent[]> }).data || {});
    } catch (_error) {
      // Silent fail - constituent check is not critical
      setConstituentFilesMap({});
      Promise.reject(_error);
    }
  };

  // Get constituent files warning data for UI rendering
  const getConstituentFilesWarningData = () => {
    return Object.keys(constituentFilesMap)
      .filter((fileId) => (constituentFilesMap[fileId]?.length as number) > 0)
      .map((fileId) => {
        const file = selectedFiles.find((f: DeleteTargetRow) => f.hierarchyId === fileId);
        const parentFileNames = constituentFilesMap[fileId]!.map((f: ConstituentParent) => f.name).join(", ");
        return {
          fileId,
          fileName: file?.name || "",
          parentFileNames,
        };
      });
  };

  // Delete files - accepts optional filesToDelete param for direct invocation
  const handleDeleteFiles = async (filesToDelete?: DeleteTargetRow[]) => {
    // Use provided files if it's an array, or fall back to ref for latest selectedFiles value
    // Note: Check Array.isArray because onClick handlers pass event objects as first arg
    const currentSelectedFiles = Array.isArray(filesToDelete) ? filesToDelete : selectedFilesRef.current;

    if (!currentSelectedFiles || currentSelectedFiles.length === 0) return { success: false };

    setIsDeleting(true);

    try {
      // Callback before delete
      onDeleteFiles();

      // Prepare files for deletion
      const selectedFilesToDelete = currentSelectedFiles
        .filter((file: DeleteTargetRow) => !isFileUpload || file.hierarchyId)
        .map((file: DeleteTargetRow) => ({
          ...file,
          spaceId: file.spaceId ?? workspaceId,
          deleteAssociatedTasks,
        }));

      if (selectedFilesToDelete.length === 0) {
        if (isFileUpload) {
          onDeleteSuccess(true);
        }
        setIsDeleting(false);
        return { success: isFileUpload };
      }

      const response = await axiosWrapper.post<FileSystemServerDeleteResponse>(
        `${getBackendUrl()}/fileSystemServer/deleteFiles`,
        {
          action: "delete",
          data: selectedFilesToDelete,
          path,
          ...(requestDirectoryTotal
            ? {
                includeDirectoryTotal: true,
                ...(directoryTotalProjectId ? { directoryTotalProjectId } : {}),
              }
            : {}),
        },
      );

      if (response.data.status) {
        onDeleteSuccess(true, response.data.totalDirectoryCount);
        dispatch(
          showToastNotification({
            content: t("All selected item(s) deleted successfully."),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        setIsDeleting(false);
        return { success: true };
      } else {
        onDeleteSuccess(false);
        const message = untranslated(response.data.message || response.data.errorMessage || "");
        dispatch(
          showToastNotification({
            content: message || t("Failed to delete"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        setIsDeleting(false);
        return { success: false, error: response.data.message };
      }
    } catch (error: unknown) {
      onDeleteSuccess(false);
      const errMsg = error instanceof Error ? error.message : undefined;
      const message = errMsg ? untranslated(errMsg) : undefined;
      dispatch(
        showToastNotification({
          content: message || t("Failed to delete"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      setIsDeleting(false);
      return { success: false, error: errMsg };
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    onCloseModal();
  };

  // Check constituent files on mount
  useEffect(() => {
    if (!isFileUpload && selectedFiles.length > 0) {
      checkIfFileIsConstituentFile();
    }
  }, []);

  // Update warning states based on constituent files
  useEffect(() => {
    const hasConstituentFiles = Object.values(constituentFilesMap).some(
      (value: ConstituentParent[]) => value?.length > 0,
    );
    if (hasConstituentFiles) {
      setShowConstituentWarning(true);
      setShowDeleteWarning(false);
    } else {
      setShowDeleteWarning(true);
      setShowConstituentWarning(false);
    }
  }, [constituentFilesMap]);

  return {
    // State
    constituentFilesMap,
    showConstituentWarning,
    showDeleteWarning,
    deleteAssociatedTasks,
    setDeleteAssociatedTasks,
    isDeleting,
    deleteWarningText,
    selectedResourceLabel,

    // Computed
    isSchedule,
    isEstimate,
    isContract,
    isFolder,

    // Handlers
    handleDeleteFiles,
    handleCancelDelete,
    checkIfFileIsConstituentFile,
    getConstituentFilesWarningData,
  };
}

export default useFileDelete;
